import { ArrowsHorizontal, Edit, InProgress, Snooze, UserAvatar } from '@carbon/icons-react';
import { ReactNode } from 'react';
import { Link } from 'react-router';

import type { ActivityAction, ActivityHistory } from 'types/activities';

type PrefCardEntityType = 'Preference Card' | 'Change Request' | 'Preference Card Item';

export type PrefCardActivity = ActivityHistory<PrefCardEntityType> & {
  actions: ActivityAction[];
};

// Preference Card
function handlePreferenceCardCreated(): ReactNode {
  return (
    <div className="flex flex-row items-center space-x-2">
      <div className="p-[4.5px]">
        <Edit />
      </div>
      <span className="mt-[2.5px] text-detail text-light">Created this preference card</span>
    </div>
  );
}

function generatePreferenceCardUpdateMessage(action: ActivityAction): ReactNode {
  let ActionIcon = Edit;
  let message: ReactNode = '';

  switch (action.value_name) {
    case 'Assignee':
      ActionIcon = UserAvatar;
      if (action.new_value === null) {
        message = (
          <div className="flex flex-row items-start space-x-2">
            <div className="p-[4.5px]">
              <ActionIcon />
            </div>
            <span className="mt-[2.5px] text-detail text-light">
              Removed <span className="text-zinc-800">Assignee</span>
            </span>
          </div>
        );
      } else {
        message =
          action.old_value === null ? (
            <div className="flex flex-row items-start space-x-2">
              <div className="p-[4.5px]">
                <ActionIcon />
              </div>
              <span className="mt-[2.5px] text-detail text-light">
                Assigned this card to <span className="text-zinc-800">{action.new_value}</span>
              </span>
            </div>
          ) : (
            <div className="flex flex-row items-start space-x-2">
              <div className="p-[4.5px]">
                <UserAvatar />
              </div>
              <span className="mt-[2.5px] text-detail text-light">
                Changed <span className="text-zinc-800">Assignee</span> from{' '}
                <span className="text-zinc-800">{action.old_value}</span> to{' '}
                <span className="text-zinc-800">{action.new_value}</span>
              </span>
            </div>
          );
      }

      break;
    case 'Clarium Status':
      ActionIcon = InProgress;
      message = (
        <div className="flex flex-row items-start space-x-2">
          <div className="p-[4.5px]">
            <ActionIcon />
          </div>
          <span className="mt-[2.5px] text-detail text-light">
            {action.new_value ? (
              <>
                {action.old_value === null ? 'Set' : 'Changed'} <span className="text-zinc-800">Status</span> to{' '}
                <span className="text-zinc-800">{action.new_value}</span>
              </>
            ) : (
              <>
                Removed the <span className="text-zinc-800">Status</span>
              </>
            )}
          </span>
        </div>
      );
      break;
    default:
      message = (
        <div className="flex flex-row items-start space-x-2">
          <div className="p-[4.5px]">
            <Edit />
          </div>
          <span className="mt-[2.5px] text-detail text-light">
            Changed <span className="text-zinc-800">{action.value_name}</span> from{' '}
            <span className="text-zinc-800">{action.old_value || 'none'}</span> to{' '}
            <span className="text-zinc-800">{action.new_value}</span>
          </span>
        </div>
      );
  }

  return message;
}

function handlePreferenceCardUpdated(activity: PrefCardActivity): ReactNode {
  return (
    <ul>
      {activity.actions.map((action, index) => (
        <li key={action.value_name}>
          {generatePreferenceCardUpdateMessage(action)}
          {index < activity.actions.length - 1 && <span className="text-detail text-light"> and </span>}
        </li>
      ))}
    </ul>
  );
}

// Change Request
function handleChangeRequestCreated(activity: PrefCardActivity): ReactNode {
  return (
    <div className="flex flex-row items-start space-x-2">
      <div className="p-[4.5px]">
        <Edit />
      </div>
      <span className="mt-[2.5px] text-detail text-light">
        Created change request{' '}
        <Link to={`requests/${activity.entity_id}`} className="text-zinc-800">
          {activity.entity_name}
        </Link>
      </span>
    </div>
  );
}

function handleChangeRequestUpdated(activity: PrefCardActivity): ReactNode {
  const filteredActions = activity.actions.filter(
    (action) =>
      action.value_name === 'Status' ||
      action.value_name === 'Reviewer' ||
      action.value_name === 'Response Type' ||
      (action.value_name === 'Response Comment' && action.new_value)
  );

  if (filteredActions.length === 0) return null;

  return (
    <ul>
      {filteredActions.map((action) => {
        let message: ReactNode;
        let descriptor: string;
        let reviewer: string;

        switch (action.value_name) {
          case 'Status':
            message = (
              <div className="flex flex-row items-start space-x-2">
                <div className="p-[4.5px]">
                  <InProgress />
                </div>
                <div className="mt-[2.5px] text-detail text-light">
                  {action.old_value === null ? 'Set' : 'Updated'} the status of{' '}
                  <Link to={`requests/${activity.entity_id}`} className="text-zinc-800">
                    {activity.entity_name || '–'}
                  </Link>{' '}
                  to <span className="text-zinc-800">{action.new_value}</span>
                </div>
              </div>
            );
            break;
          case 'Reviewer':
            descriptor = action.new_value ? 'Added' : 'Removed';
            reviewer = action.new_value || action.old_value || '–';
            message = (
              <div className="flex flex-row items-start space-x-2">
                <div className="p-[4.5px]">
                  <UserAvatar />
                </div>
                <div className="mt-[2.5px] text-detail text-light">
                  {descriptor} <span className="text-zinc-800">{reviewer}</span> as a reviewer on{' '}
                  <Link to={`requests/${activity.entity_id}`} className="text-zinc-800">
                    {activity.entity_name || '–'}
                  </Link>
                </div>
              </div>
            );
            break;
          case 'Response Type':
            message = (
              <div className="flex flex-row items-start space-x-2">
                <div className="p-[4.5px]">
                  <ArrowsHorizontal />
                </div>
                <div className="mt-[2.5px] text-detail text-light">
                  Added a review on{' '}
                  <Link to={`requests/${activity.entity_id}`} className="text-zinc-800">
                    {activity.entity_name || '–'}
                  </Link>
                  : <span className="text-zinc-800">{action.new_value}</span>
                </div>
              </div>
            );
            break;
          case 'Response Comment':
            message = (
              <div className="flex flex-row items-start space-x-2">
                <div className="p-[4.5px]">
                  <div className="h-4 w-4"></div>
                </div>
                <div className="mt-[2.5px] w-full text-detail text-light">
                  and added a comment:{' '}
                  <div className="my-2 inline-block w-full whitespace-pre-line rounded bg-zinc-100 px-2 py-1.5 text-regular text-zinc-800 sm:w-full [&_.at-m]:bg-transparent [&_.at-m]:font-m [&_.at-m]:text-primary">
                    {action.new_value}
                  </div>
                </div>
              </div>
            );
            break;
          default:
            message = null;
        }
        return <li key={action.value_name}>{message}</li>;
      })}
    </ul>
  );
}

// Preference Card Item
function handlePreferenceCardItemUpdated(activity: PrefCardActivity): ReactNode {
  const isSnoozed = activity.actions.some(
    (action) => action.value_name === 'Snooze Until' && action.new_value && action.old_value === null
  );

  const isUnSnoozed = activity.actions.some(
    (action) =>
      action.value_name === 'Snooze Until' && action.old_value && action.old_value !== null && action.new_value === null
  );

  if (!isSnoozed && !isUnSnoozed) {
    return null;
  }

  return (
    <div className="flex flex-row items-start space-x-2">
      <div className="p-[4.5px]">
        <Snooze />
      </div>
      <span className="mt-[2.5px] text-detail text-light">
        {isSnoozed ? 'Snoozed' : 'Unsnoozed'} a recommendation for{' '}
        <span className="text-zinc-800">Item {activity.entity_name || '–'}</span>
      </span>
    </div>
  );
}

export function createActivity(activity: PrefCardActivity): ReactNode {
  const handlers: Record<
    string,
    Partial<Record<'Created' | 'Updated' | 'Deleted', (activity: PrefCardActivity) => ReactNode>>
  > = {
    'Preference Card': {
      Created: handlePreferenceCardCreated,
      Updated: handlePreferenceCardUpdated,
    },
    'Change Request': {
      Created: handleChangeRequestCreated,
      Updated: handleChangeRequestUpdated,
    },
    'Preference Card Item': {
      Updated: handlePreferenceCardItemUpdated,
    },
  };

  const entityHandler = handlers[activity.entity_type];
  if (!entityHandler) {
    console.warn(`Unhandled entity type: ${activity.entity_type}`);
    return 'Unknown';
  }

  const operationHandler = entityHandler[activity.state_operation];
  if (!operationHandler) {
    console.warn(`Unhandled operation ${activity.state_operation} for entity type ${activity.entity_type}`);
    return 'Unknown';
  }

  return operationHandler(activity);
}
